import React, { useState, useEffect, useCallback } from 'react';
import CurrentVersion from './CurrentVersion';
import NewVersionPreview from './NewVersionPreview';
import ControlPanel from './ControlPanel';
import AvailablePuzzles from './AvailablePuzzles';
import { getDifficulty } from '../utils/puzzleUtils';
import { versionAPI, puzzleAPI } from '../services/api';
import './VersionManager.css';

const VersionManager = () => {
  const [latestVersion, setLatestVersion] = useState(null);
  const [currentVersionPuzzles, setCurrentVersionPuzzles] = useState([]);
  const [previewPuzzles, setPreviewPuzzles] = useState([]);
  const [availablePuzzles, setAvailablePuzzles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLatestVersion = useCallback(async () => {
    try {
      const data = await versionAPI.getLatestVersion();
      setLatestVersion(data);
      fetchVersionPuzzles(data.version);
    } catch (err) {
      setError('Failed to fetch latest version');
      console.error(err);
    }
  }, []);  // Empty dependency array as it doesn't depend on any props or state
  
  const fetchVersionPuzzles = async (version) => {
    try {
      const data = await versionAPI.getVersionPuzzles(version);
      setCurrentVersionPuzzles(data);
      setPreviewPuzzles(data); // Initialize preview with current version puzzles
    } catch (err) {
      setError('Failed to fetch version puzzles');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllPuzzles = useCallback(async () => {
    try {
      const data = await puzzleAPI.getAllPuzzles();
      setAvailablePuzzles(data);
    } catch (err) {
      setError('Failed to fetch all puzzles');
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchLatestVersion();
    fetchAllPuzzles();
  }, [fetchLatestVersion, fetchAllPuzzles]);


  const addPuzzleToPreview = useCallback((puzzleToAdd) => {
    setPreviewPuzzles(prev => {
      if (!prev.some(p => p.board_number === puzzleToAdd.board_number)) {
        return [...prev, puzzleToAdd];
      }
      return prev;
    });
  }, []);

  const removePuzzleFromPreview = useCallback((puzzleToRemove) => {
    setPreviewPuzzles(prev => prev.filter(p => p.board_number !== puzzleToRemove.board_number));
  }, []);

  const resetPreview = useCallback(() => {
    setPreviewPuzzles([...currentVersionPuzzles]);
  }, [currentVersionPuzzles]);

  const reorderPuzzles = useCallback((newOrder) => {
    setPreviewPuzzles(newOrder);
  }, []);

  const createNewVersion = async () => {
    try {
      const boardNumbers = previewPuzzles.map(puzzle => puzzle.board_number);
      const data = await versionAPI.createNewVersion(boardNumbers);
      alert(`New version created: ${data.version_name}`);
      fetchLatestVersion();
    } catch (err) {
      setError('Failed to create new version');
      console.error(err);
    }
  };

  const compareVersions = useCallback(() => {
    const added = previewPuzzles.filter(p => !currentVersionPuzzles.some(cp => cp.board_number === p.board_number));
    const removed = currentVersionPuzzles.filter(p => !previewPuzzles.some(pp => pp.board_number === p.board_number));
    
    const difficultyChanges = ['Easy', 'Medium', 'Hard', 'Expert'].reduce((acc, difficulty) => {
      const currentCount = currentVersionPuzzles.filter(p => getDifficulty(p.fice_score) === difficulty).length;
      const previewCount = previewPuzzles.filter(p => getDifficulty(p.fice_score) === difficulty).length;
      acc[difficulty] = previewCount - currentCount;
      return acc;
    }, {});

    return { added, removed, difficultyChanges };
  }, [currentVersionPuzzles, previewPuzzles]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="version-manager">
      <h1>Version Manager</h1>
      <div className="version-comparison">
        <CurrentVersion 
          puzzles={currentVersionPuzzles} 
          latestVersion={latestVersion} 
        />
        <NewVersionPreview 
          puzzles={previewPuzzles} 
          onRemovePuzzle={removePuzzleFromPreview}
          onReorderPuzzles={reorderPuzzles}
        />
      </div>
      <ControlPanel 
        onResetPreview={resetPreview}
        onCreateNewVersion={createNewVersion}
        comparisonData={compareVersions()}
      />
      <AvailablePuzzles 
        puzzles={availablePuzzles.filter(p => !previewPuzzles.some(pp => pp.board_number === p.board_number))}
        onAddPuzzle={addPuzzleToPreview}
      />
    </div>
  );
};

export default VersionManager;
